'use client';
import React, { useState, useEffect } from 'react';
import { AddToCart as SharedAddToCart } from '@launchstore/shared-puck';
import type { AddToCartProps, AddToCartFn } from '@launchstore/shared-puck';
import { useProduct, useVariantSelection } from '@/lib/hooks/useProduct';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import { useCart } from '@/lib/hooks/useCart';
import { isPreorder, formatPreorderDate } from '@/lib/util/is-preorder';
import { fetchVariantPreorder } from '@/lib/util/fetch-variant-preorder';
import { getMedusaBackendUrl, getMedusaPublishableKey } from '@/lib/config';
import { findFirstPurchasableVariant } from '@/lib/util/variant-stock';

const SharedAddToCartRender = (SharedAddToCart as any).render;

const AddToCartImpl: React.FC<AddToCartProps> = (props: any) => {
  const { product, countryCode, theme } = useProduct() as any;
  const { selectedVariant, quantity } = useVariantSelection() as any;
  const { context } = usePuckContext() as any;
  const isCard = !!context?.productCard;
  const { addItem, isAdding } = useCart() as any;
  const [preorderVariant, setPreorderVariant] = useState<any>(null);

  useEffect(() => {
    if (!selectedVariant?.id) { setPreorderVariant(null); return; }
    let cancelled = false;
    fetchVariantPreorder(selectedVariant.id, getMedusaBackendUrl(), getMedusaPublishableKey() ?? undefined)
      .then((pv: any) => { if (!cancelled) setPreorderVariant(pv); });
    return () => { cancelled = true; };
  }, [selectedVariant?.id]);

  const variantIsPreorder = isPreorder(preorderVariant);

  // Card mode, multi-variant, nothing picked: route to the product page
  // ("View Options") instead of silently adding an arbitrary variant — the
  // recommended pattern (Shopify Dawn et al.). Picking a variant via the
  // card's option pills sets selectedVariant, which auto-switches the button
  // into a live Add to Cart. When NO variant is purchasable, the disabled
  // "Out of Stock" button wins over everything.
  const variants: any[] = Array.isArray(product?.variants) ? product.variants : [];
  const cardNeedsViewOptions = isCard && !selectedVariant && variants.length > 1;
  const viewOptionsHref =
    cardNeedsViewOptions && findFirstPurchasableVariant(variants) && product?.handle
      ? `/products/${product.handle}`
      : undefined;

  const effectiveVariant: any =
    selectedVariant ?? (variants.length > 0 ? variants[0] : null);

  const inStock = (() => {
    if (cardNeedsViewOptions && !viewOptionsHref) {
      // Multi-variant card where every variant is out of stock (and none
      // allow backorder) — show the dead state instead of View Options.
      return false;
    }
    // Use effectiveVariant (selected OR first/default) so the live render
    // branch's `!inStock` is computed against a real variant, not a null.
    if (effectiveVariant) {
      if (variantIsPreorder) return true;
      // Inventory not managed for this variant → always purchasable.
      // NOTE: use `=== false` so an unloaded/undefined value is treated as
      // "managed" and falls through to the stock check (never assume in-stock).
      if (effectiveVariant.manage_inventory === false) return true;
      // Backorder allowed → purchasable even at 0 stock.
      if (effectiveVariant.allow_backorder) return true;
      // Managed inventory → in stock only when quantity > 0.
      if ((effectiveVariant.inventory_quantity ?? 0) > 0) return true;
      return false;
    }
    // No variant available at all (no product, no variants) — default to
    // in-stock so the editor preview shows a usable button.
    return true;
  })();

  const handleAdd: AddToCartFn = async (variantId, q) => {
    await addItem({ variantId, quantity: q, countryCode: countryCode || 'us' });
  };

  return (
    <SharedAddToCartRender
      {...(props as any)}
      selectedVariantId={effectiveVariant?.id ?? null}
      quantity={quantity || 1}
      onAdd={handleAdd}
      isLoading={!!isAdding}
      inStock={inStock}
      isPreorder={variantIsPreorder}
      preorderAvailableDate={preorderVariant?.available_date}
      theme={theme}
      viewOptionsHref={viewOptionsHref}
    />
  );
};

export const AddToCart: any = { ...SharedAddToCart, render: (props: any) => <AddToCartImpl {...props} /> };
export default AddToCart;
